import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Mountain, Camera, Inbox, FolderHeart, Search } from "lucide-react";

export default function Welcome() {
  const { from_screen_name } = useParams();
  const [inviter, setInviter] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!from_screen_name) { setChecked(true); return; }
    (async () => {
      try {
        const { data } = await api.get(`/profile/${from_screen_name}`);
        setInviter(data);
      } catch { /* unknown inviter, still show generic welcome */ }
      finally { setChecked(true); }
    })();
  }, [from_screen_name]);

  const rememberInviter = () => {
    if (from_screen_name) {
      try { localStorage.setItem("mp_invited_by", from_screen_name); } catch { /* ignore */ }
    }
  };

  const features = [
    { icon: Camera, title: "Friends post your photos", text: "People who know you can upload pictures of you — no more lost memories scattered across phones." },
    { icon: Inbox, title: "You review everything", text: "Every photo lands in your private queue first. Nothing goes public until you approve it." },
    { icon: FolderHeart, title: "Build your albums", text: "Organize approved photos into albums and decide what's public and what stays private." },
    { icon: Search, title: "You control privacy", text: "Only your screen name is searchable by default. Hometown, schools and more are opt-in." },
  ];

  return (
    <div className="paper-grain min-h-screen" data-testid="welcome-page">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex items-center gap-2 mb-6">
          <Mountain className="w-6 h-6" style={{ color: "var(--mp-terracotta)" }}/>
          <span className="font-script text-2xl text-[#5C5C5C]">you're invited</span>
        </div>

        {inviter ? (
          <p className="text-[#5C5C5C] mb-2" data-testid="welcome-inviter">
            <strong className="text-[#3A5A40]">@{inviter.screen_name}</strong>
            {inviter.name ? ` (${inviter.name})` : ""} thinks you'll love MemoryPeaks.
          </p>
        ) : (
          checked && <p className="text-[#5C5C5C] mb-2" data-testid="welcome-inviter">A friend thinks you'll love MemoryPeaks.</p>
        )}

        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mb-4">
          Your memories, gathered in one place.
        </h1>
        <p className="text-[#5C5C5C] text-base max-w-2xl mb-10">
          MemoryPeaks is where friends share photos of <em>you</em> — and you decide what to keep.
          Create a free account, claim your screen name, and start collecting the moments other people captured.
        </p>

        <div className="flex flex-wrap gap-3 mb-16">
          <Link to="/register" onClick={rememberInviter} className="btn-primary" data-testid="welcome-signup-btn">
            Create my free account
          </Link>
          <Link to="/login" className="btn-secondary" data-testid="welcome-login-btn">
            I already have an account
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
          {features.map((f) => (
            <div key={f.title} className="paper-card p-6" data-testid={`welcome-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <f.icon className="w-6 h-6 text-[#3A5A40] mb-3"/>
              <h3 className="font-serif-display text-xl mb-1">{f.title}</h3>
              <p className="text-sm text-[#5C5C5C]">{f.text}</p>
            </div>
          ))}
        </div>

        <div className="paper-card p-8 text-center">
          <p className="font-script text-2xl text-[#8C3A3A] mb-2">ready when you are</p>
          <h2 className="font-serif-display text-3xl mb-4">Claim your name in under a minute.</h2>
          <Link to="/register" onClick={rememberInviter} className="btn-primary inline-flex" data-testid="welcome-signup-btn-bottom">
            Get started — it's free
          </Link>
          {inviter && (
            <p className="text-xs text-[#8A8A8A] mt-4">
              Want to see what it looks like?{" "}
              <Link to={`/u/${inviter.screen_name}`} className="underline" data-testid="welcome-view-inviter-link">
                View @{inviter.screen_name}'s profile
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
